import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useOnboardingTemplates } from '@/hooks/useOnboardingTemplates';
import { OnboardingBlockRenderer } from './onboarding/OnboardingBlockRenderer';
import { getIconByName } from './onboarding/iconMap';

interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onboardingType: string; // 'ambassador' | 'co_organizer' | 'speaker' | 'volunteer'
  eventName: string;
  entityPermissionId?: string;
  /** For speaker/volunteer: complete via onboarding_completions instead of entity_permissions */
  completionUserId?: string;
  completionEntityId?: string;
}

export const OnboardingWizard = ({
  open,
  onClose,
  onboardingType,
  eventName,
  entityPermissionId,
  completionUserId,
  completionEntityId,
}: OnboardingWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { templates, loading } = useOnboardingTemplates(onboardingType);

  // Reset step when opening
  useEffect(() => {
    if (open) setCurrentStep(0);
  }, [open]);

  if (!open) return null;

  const steps = templates;
  if (loading) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (steps.length === 0) {
    onClose();
    return null;
  }

  const progress = ((currentStep + 1) / steps.length) * 100;
  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  // Replace {eventName} placeholders in title/subtitle
  const title = step.title.replace(/\{eventName\}/g, eventName);
  const subtitle = step.subtitle.replace(/\{eventName\}/g, eventName);

  const handleComplete = async () => {
    // Mark completion via entity_permissions (co-organizer/ambassador)
    if (entityPermissionId) {
      await supabase
        .from('entity_permissions')
        .update({ onboarding_completed: true } as any)
        .eq('id', entityPermissionId);
    }
    // Mark completion via onboarding_completions (speaker/volunteer)
    if (completionUserId && completionEntityId) {
      await supabase.from('onboarding_completions').upsert({
        user_id: completionUserId,
        onboarding_type: onboardingType,
        entity_id: completionEntityId,
      } as any, { onConflict: 'user_id,onboarding_type,entity_id' });
    }
    onClose();
  };

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleComplete()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        <Progress value={progress} className="h-1 rounded-none rounded-t-lg" />

        <div className="p-6 space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              {getIconByName(step.icon_name)}
            </div>
            <DialogTitle className="text-2xl font-bold">{title}</DialogTitle>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>

          <OnboardingBlockRenderer blocks={step.content_blocks} />

          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" onClick={handleBack} disabled={currentStep === 0} className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <span className="text-xs text-muted-foreground">
              {currentStep + 1} / {steps.length}
            </span>
            <Button onClick={handleNext} className="gap-1">
              {isLast ? 'Get Started' : 'Next'}
              {!isLast && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
